import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Card } from '../components/UI/Card';
import Button from '../components/UI/Button';
import { Input, Textarea, Select } from '../components/UI/Input';
import { api } from '../services/api';
import './Templates.css';

// ─── Actions automatiques ──────────────────────────────────────────────────────
const ACTIONS = [
  { id: 'birthday',     icon: '🎂', label: 'Anniversaire',   desc: 'Envoyé le jour J',           color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D', defaultBase: 'base_birthday' },
  { id: 'reactivation', icon: '💤', label: 'Réactivation',   desc: 'Contact inactif +6 mois',    color: '#5A35FF', bg: '#EDE9FF', border: '#A78BFA', defaultBase: 'base_reactivation' },
  { id: 'loyalty',      icon: '⭐', label: 'Fidélisation',   desc: 'Clients fidèles',            color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7', defaultBase: 'base_loyalty' },
  { id: 'promo',        icon: '🎯', label: 'Offre spéciale', desc: 'Campagnes promotionnelles',  color: '#EF4444', bg: '#FEF2F2', border: '#FCA5A5', defaultBase: 'base_promo' },
];

// ─── Canaux ───────────────────────────────────────────────────────────────────
const CHANNELS = [
  { id: 'all',     label: 'Tous',          icon: '📋' },
  { id: 'email',   label: 'Email SMTP',    icon: '✉️' },
  { id: 'mailjet', label: 'Email Mailjet', icon: '📧' },
  { id: 'sms',     label: 'SMS',           icon: '📱' },
  { id: 'landing', label: 'Landing',       icon: '🌐' },
];

// ─── Pastilles canal ──────────────────────────────────────────────────────────
const CHANNEL_META = {
  email:   { label: 'Email SMTP',    icon: '✉️',  color: '#5A35FF', bg: '#EDE9FF', border: '#A78BFA', dot: '#7C3AED' },
  mailjet: { label: 'Mailjet',       icon: '📧',  color: '#0369A1', bg: '#E0F2FE', border: '#7DD3FC', dot: '#0284C7' },
  sms:     { label: 'SMS',           icon: '📱',  color: '#065F46', bg: '#D1FAE5', border: '#6EE7B7', dot: '#10B981' },
  landing: { label: 'Landing',       icon: '🌐',  color: '#92400E', bg: '#FEF3C7', border: '#FCD34D', dot: '#F59E0B' },
};

function ChannelPastille({ channel, size = 'sm' }) {
  const ch = channel || 'email';
  const m  = CHANNEL_META[ch] || CHANNEL_META.email;
  return (
    <span
      className={`tpl-pastille tpl-pastille-${size}`}
      style={{ '--pc': m.color, '--pb': m.bg, '--pbr': m.border, '--pdot': m.dot }}
    >
      <span className="tpl-pastille-dot" />
      {m.icon} {m.label}
    </span>
  );
}

// ─── Types de templates ────────────────────────────────────────────────────────
const TYPE_LABELS = {
  birthday:     '🎂 Anniversaire',
  reactivation: '💤 Réactivation',
  promo:        '🎯 Promotion',
  loyalty:      '⭐ Fidélisation',
  custom:       '✉️ Personnalisé',
};

// ─── Templates de base livrés avec Appee ──────────────────────────────────────
const BASE_TEMPLATES = [
  {
    _baseId: 'base_birthday', icon: '🎂', name: 'Joyeux anniversaire',
    type: 'birthday', tone: 'vouvoiement', channel: 'email',
    color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D',
    subject: '🎂 Joyeux anniversaire {{prénom}} — un cadeau vous attend !',
    body: `Cher(e) {{prénom}},\n\nToute l'équipe de {{entreprise}} vous souhaite un très joyeux anniversaire ! 🎉\n\nEn ce jour spécial, nous voulions vous dire combien votre fidélité compte pour nous. Vous méritez le meilleur.\n\nJoyeux anniversaire,\n{{entreprise}}`,
  },
  {
    _baseId: 'base_reactivation', icon: '💤', name: 'Vous nous manquez',
    type: 'reactivation', tone: 'vouvoiement', channel: 'email',
    color: '#5A35FF', bg: '#EDE9FF', border: '#A78BFA',
    subject: '💙 {{prénom}}, vous nous manquez !',
    body: `Bonjour {{prénom}},\n\nCela fait un moment que nous n'avons pas eu le plaisir de vous voir, et nous voulions prendre de vos nouvelles.\n\nVotre satisfaction est notre priorité. Si vous avez des questions ou si nous pouvons vous aider, n'hésitez pas à nous contacter.\n\nNous espérons vous revoir bientôt,\n{{entreprise}}`,
  },
  {
    _baseId: 'base_loyalty', icon: '⭐', name: 'Merci pour votre fidélité',
    type: 'loyalty', tone: 'vouvoiement', channel: 'email',
    color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7',
    subject: '⭐ Un grand merci, {{prénom}} !',
    body: `Cher(e) {{prénom}},\n\nNous voulions simplement prendre un moment pour vous remercier.\n\nVotre fidélité et votre confiance sont pour nous la plus belle des récompenses. C'est grâce à des clients comme vous que notre travail prend tout son sens.\n\nMerci du fond du cœur,\n{{entreprise}}`,
  },
  {
    _baseId: 'base_promo', icon: '🎁', name: 'Offre spéciale client',
    type: 'promo', tone: 'vouvoiement', channel: 'email',
    color: '#EF4444', bg: '#FEF2F2', border: '#FCA5A5',
    subject: '🎁 Une offre rien que pour vous, {{prénom}}',
    body: `Bonjour {{prénom}},\n\nParce que vous comptez pour nous, nous avons préparé une offre exclusive réservée à nos meilleurs clients.\n\nN'attendez pas trop longtemps — cette offre est limitée dans le temps.\n\nÀ très bientôt,\n{{entreprise}}`,
  },
];

// ─── Inspirations saisonnières ─────────────────────────────────────────────────
const SEASONAL_TEMPLATES = [
  { season: 'Saint-Valentin',      icon: '💝', color: '#DB2777', bg: '#FDF2F8', border: '#F9A8D4', name: 'Message Saint-Valentin',    type: 'promo',    tone: 'vouvoiement', subject: '💝 Une attention spéciale pour vous, {{prénom}}',       body: `Bonjour {{prénom}},\n\nEn ce 14 février, nous voulions prendre un moment pour vous témoigner notre gratitude.\n\nVotre fidélité nous touche profondément. C'est grâce à des clients comme vous que notre travail prend tout son sens.\n\nAvec toute notre affection,\n{{entreprise}}` },
  { season: 'Fête des Mères',      icon: '🌸', color: '#7C3AED', bg: '#F5F3FF', border: '#C4B5FD', name: 'Fête des Mères',            type: 'loyalty',  tone: 'vouvoiement', subject: '🌸 Bonne fête, {{prénom}} !',                           body: `Chère {{prénom}},\n\nÀ l'occasion de la Fête des Mères, nous tenions à vous adresser nos plus sincères félicitations et vous remercier pour votre confiance.\n\nVous méritez d'être choyée — aujourd'hui plus que jamais.\n\nAvec tout notre respect,\n{{entreprise}}` },
  { season: 'Noël',                icon: '🎄', color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7', name: 'Voeux de Noël',             type: 'loyalty',  tone: 'vouvoiement', subject: '🎄 Joyeux Noël, {{prénom}} !',                          body: `Cher(e) {{prénom}},\n\nÀ l'approche des fêtes de fin d'année, toute notre équipe vous adresse ses voeux les plus chaleureux.\n\nMerci pour votre confiance tout au long de cette année.\n\nJoyeuses fêtes,\n{{entreprise}}` },
  { season: 'Nouvel An',           icon: '🎆', color: '#5A35FF', bg: '#EDE9FF', border: '#A78BFA', name: 'Bonne Année',               type: 'loyalty',  tone: 'vouvoiement', subject: '🎆 Bonne Année {{prénom}} !',                           body: `Cher(e) {{prénom}},\n\nEn ce début d'année, toute l'équipe de {{entreprise}} vous souhaite une excellente année pleine de santé, de bonheur et de réussite.\n\nAvec nos meilleurs voeux,\n{{entreprise}}` },
  { season: 'Rentrée',             icon: '📚', color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D', name: 'Message de rentrée',        type: 'promo',    tone: 'vouvoiement', subject: '📚 La rentrée est là, {{prénom}} — et nous aussi !',    body: `Bonjour {{prénom}},\n\nLa rentrée est une excellente occasion de prendre de nouvelles habitudes.\n\nNous avons de belles nouveautés à vous présenter !\n\nÀ très bientôt,\n{{entreprise}}` },
  { season: 'Été',                 icon: '☀️', color: '#EF4444', bg: '#FEF2F2', border: '#FCA5A5', name: "Message d'été",             type: 'custom',   tone: 'vouvoiement', subject: "☀️ Profitez de l'été, {{prénom}} !",                    body: `Cher(e) {{prénom}},\n\nL'été est là et c'est le moment idéal pour prendre soin de vous.\n\nNous espérons que vous profitez pleinement de cette belle saison.\n\nBel été,\n{{entreprise}}` },
  { season: 'Pâques',              icon: '🐣', color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7', name: 'Joyeuses Pâques',          type: 'custom',   tone: 'vouvoiement', subject: '🐣 Joyeuses Pâques, {{prénom}} !',                     body: `Cher(e) {{prénom}},\n\nÀ l'occasion de Pâques, toute notre équipe vous souhaite de joyeuses fêtes, en famille ou entre amis.\n\nChaleureusement,\n{{entreprise}}` },
  { season: 'Anniversaire fidélité', icon: '🎊', color: '#FFC400', bg: '#FFFBEB', border: '#FCD34D', name: 'Anniversaire client',    type: 'birthday', tone: 'vouvoiement', subject: '🎊 Joyeux anniversaire {{prénom}} — un cadeau vous attend !', body: `Cher(e) {{prénom}},\n\nToute l'équipe de {{entreprise}} vous souhaite un très joyeux anniversaire !\n\nEn ce jour spécial, nous voulions vous dire combien votre fidélité compte pour nous.\n\nJoyeux anniversaire,\n{{entreprise}}` },
];

// ─── Utilitaires ──────────────────────────────────────────────────────────────
function insertAtCursor(ref, text, onChange, val) {
  const el = ref.current;
  if (!el) { onChange({ target: { value: val + text } }); return; }
  const s = el.selectionStart, e = el.selectionEnd;
  const nv = val.slice(0, s) + text + val.slice(e);
  onChange({ target: { value: nv } });
  setTimeout(() => { el.focus(); el.setSelectionRange(s + text.length, s + text.length); }, 0);
}

function sanitizeHtml(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('*').forEach(el => {
    [...el.attributes].forEach(attr => { if (attr.name.startsWith('on')) el.removeAttribute(attr.name); });
    if (el.tagName === 'A' && el.href) {
      try {
        const u = new URL(el.href, window.location.href);
        if (!['https:', 'http:', 'mailto:'].includes(u.protocol)) el.removeAttribute('href');
      } catch { el.removeAttribute('href'); }
    }
    if (el.tagName === 'IMG') {
      const src = el.getAttribute('src') || '';
      if (!src.startsWith('data:image/') && !src.startsWith('https://') && !src.startsWith('http://')) el.removeAttribute('src');
    }
  });
  return div.innerHTML;
}

const ESC = c => ({ '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '&': '&amp;' }[c]);

// ─── Composant principal ───────────────────────────────────────────────────────
export default function Templates() {
  const { templates, setTemplates, addNotification, tenant, showQuotaError } = useApp();
  const navigate = useNavigate();

  // État éditeur
  const [selected,       setSelected]       = useState(null);
  const [form,           setForm]           = useState(null);
  const [showNew,        setShowNew]        = useState(false);
  const [activeBodyTab,  setActiveBodyTab]  = useState('edit');
  const [saving,         setSaving]         = useState(false);
  const [deleting,       setDeleting]       = useState(false);
  const [generating,     setGenerating]     = useState(false);
  const bodyRef    = useRef(null);
  const imgInputRef = useRef(null);

  // Filtres
  const [search,        setSearch]        = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [libOpen,       setLibOpen]       = useState(false);

  // État SMS (solde, inclus mensuel, anniversaire SMS, dernier envoi) — visibilité pour le user
  const [smsStatus, setSmsStatus] = useState(null);
  useEffect(() => {
    let alive = true;
    api.getSmsBalance().then(r => { if (alive) setSmsStatus(r); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Actions par défaut (localStorage)
  const [defaultActions, setDefaultActions] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('appee_default_templates') || '{}');
      const result = {};
      ACTIONS.forEach(a => { result[a.id] = saved[a.id] ?? a.defaultBase; });
      return result;
    } catch {
      return Object.fromEntries(ACTIONS.map(a => [a.id, a.defaultBase]));
    }
  });
  const [assigningAction, setAssigningAction] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Tous les templates (base + user) ──────────────────────────────────────────
  const allItems = useMemo(() => [
    ...BASE_TEMPLATES.map(t => ({ ...t, _key: t._baseId, _isBase: true })),
    ...templates.map(t => ({ ...t, _key: String(t.id), _isBase: false, channel: t.channel || 'email' })),
  ], [templates]);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const getById = (id) => {
    if (!id) return null;
    const base = BASE_TEMPLATES.find(t => t._baseId === id);
    if (base) return base;
    return templates.find(t => String(t.id) === String(id)) || null;
  };

  const isInProd = (t) => {
    const id = t._baseId || String(t.id);
    return ACTIONS.some(a => String(defaultActions[a.id]) === String(id));
  };

  const getActionFor = (t) => {
    const id = t._baseId || String(t.id);
    return ACTIONS.find(a => String(defaultActions[a.id]) === String(id)) || null;
  };

  const tplChannel = (t) => t.channel || 'email';

  // ── Items filtrés ─────────────────────────────────────────────────────────────
  const filteredItems = useMemo(() => allItems.filter(t => {
    if (channelFilter !== 'all' && tplChannel(t) !== channelFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!t.name?.toLowerCase().includes(q) && !t.subject?.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [allItems, channelFilter, search]);

  // ── Sauvegarder action par défaut ─────────────────────────────────────────────
  const saveDefaultAction = (actionId, templateId) => {
    const updated = { ...defaultActions, [actionId]: templateId };
    setDefaultActions(updated);
    localStorage.setItem('appee_default_templates', JSON.stringify(updated));
    setAssigningAction(null);
    addNotification('Template par défaut mis à jour ✅');
  };

  // ── Éditeur : ouvrir ──────────────────────────────────────────────────────────
  const handleEdit = (t) => {
    if (t._isBase) {
      // Chercher si une version utilisateur existe déjà pour ce type
      const existingUserTpl = templates.find(ut => ut._fromBase === t._baseId);
      if (existingUserTpl) {
        // Éditer la version utilisateur existante
        setSelected(existingUserTpl.id);
        setForm({ ...existingUserTpl, channel: existingUserTpl.channel || 'email' });
        setShowNew(false);
      } else {
        // Charger le template de base pour personnalisation
        const action = ACTIONS.find(a => a.defaultBase === t._baseId);
        setForm({
          name: t.name, type: t.type, tone: t.tone || 'vouvoiement',
          subject: t.subject, body: t.body, channel: t.channel || 'email',
          _fromBase: t._baseId,
          _fromActionId: action?.id || null,
        });
        setSelected(null);
        setShowNew(true);
      }
    } else {
      setSelected(t.id);
      setForm({ ...t, channel: t.channel || 'email' });
      setShowNew(false);
    }
    setActiveBodyTab('edit');
  };

  const handleNew = (channel = 'email') => {
    setSelected(null);
    setForm({ name: '', type: 'custom', tone: 'vouvoiement', subject: '', body: '', channel });
    setShowNew(true);
    setActiveBodyTab('edit');
  };

  // ── Sauvegarder template ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name?.trim()) { addNotification('Donnez un nom au template.', 'error'); return; }
    setSaving(true);
    const { _fromBase, _fromActionId, ...cleanForm } = form;
    try {
      if (showNew) {
        const created = await api.createTemplate({ ...cleanForm, _fromBase });
        setTemplates(ts => [...ts, created]);
        // Auto-assigner à l'action correspondante si chargé depuis un template de base
        if (_fromActionId) {
          const newId = created.id ? String(created.id) : null;
          if (newId) {
            const updated = { ...defaultActions, [_fromActionId]: newId };
            setDefaultActions(updated);
            localStorage.setItem('appee_default_templates', JSON.stringify(updated));
          }
        }
        addNotification(_fromBase ? `Template enregistré et défini par défaut ✅` : 'Template créé ✅');
      } else {
        const updated = await api.updateTemplate(selected, cleanForm);
        setTemplates(ts => ts.map(t => t.id === selected ? { ...t, ...updated } : t));
        addNotification('Template mis à jour ✅');
      }
      setForm(null); setSelected(null); setShowNew(false);
    } catch {
      const fallbackId = Date.now();
      if (showNew) {
        setTemplates(ts => [...ts, { ...cleanForm, _fromBase, id: fallbackId }]);
        if (_fromActionId) {
          const updated = { ...defaultActions, [_fromActionId]: String(fallbackId) };
          setDefaultActions(updated);
          localStorage.setItem('appee_default_templates', JSON.stringify(updated));
        }
      } else {
        setTemplates(ts => ts.map(t => t.id === selected ? { ...t, ...cleanForm } : t));
      }
      addNotification(showNew ? (_fromBase ? 'Template enregistré ✅' : 'Template créé ✅') : 'Mis à jour ✅');
      setForm(null); setSelected(null); setShowNew(false);
    } finally { setSaving(false); }
  };

  // ── Supprimer template ────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce template définitivement ?')) return;
    setDeleting(true);
    try { await api.deleteTemplate(id); } catch {}
    setTemplates(ts => ts.filter(t => t.id !== id));
    if (selected === id) { setForm(null); setSelected(null); }
    addNotification('Template supprimé.');
    setDeleting(false);
  };

  // ── Génération IA ─────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenerating(true);
    const isSms     = form.channel === 'sms';
    const objective = form.type || 'custom';
    const tone      = form.tone || 'vouvoiement';
    try {
      if (isSms) {
        // SMS : endpoint dédié qui gère le profil entreprise + contrainte 160 car.
        const res = await api.generateSmsMessage(objective, tone);
        let smsBody = (res.sms_body || '').replace(/\n{2,}/g, '\n').trim();
        // Sécurité : tronquer à 155 chars pour laisser de la place aux variables
        if (smsBody.length > 155) {
          smsBody = smsBody.slice(0, 152).replace(/\s+\S*$/, '') + '…';
        }
        set('body', smsBody);
        addNotification(`SMS généré — ${smsBody.length} caractères ✨`);
      } else {
        // Email : objective, segmentInfo (null), tone — 3 paramètres exacts de l'API
        const { subject, body } = await api.generateMessage(objective, null, tone);
        set('subject', subject);
        set('body', body);
        addNotification("Message généré par l'IA ✨");
      }
    } catch (err) {
      showQuotaError(err);
      if (!err?.quotaExceeded) {
        if (isSms) {
          const fallbacks = {
            birthday:     'Bonjour {{prénom}} ! Toute l\'équipe {{entreprise}} vous souhaite un joyeux anniversaire 🎂 Que cette journée soit mémorable !',
            reactivation: 'Bonjour {{prénom}}, vous nous manquez ! Revenez chez {{entreprise}}, on a de belles nouveautés pour vous 😊',
            loyalty:      'Merci {{prénom}} pour votre fidélité ! {{entreprise}} vous réserve une surprise. À très bientôt !',
            promo:        'Bonjour {{prénom}} ! Offre exclusive pour vous chez {{entreprise}}. Profitez-en maintenant, places limitées 🎯',
          };
          const smsBody = fallbacks[form.type] || `Bonjour {{prénom}}, un message de {{entreprise}} pour vous. Passez nous voir bientôt !`;
          set('body', smsBody);
          addNotification(`SMS généré — ${smsBody.length} caractères ✨`);
        } else {
          set('subject', 'Un message personnalisé pour vous, {{prénom}}');
          set('body', 'Bonjour {{prénom}},\n\nNous espérons que vous allez bien.\n\nÀ très bientôt,\n{{entreprise}}');
          addNotification('Message généré ✨');
        }
      }
    } finally { setGenerating(false); }
  };

  const handleInsertVariable = (v) => insertAtCursor(bodyRef, v, e => set('body', e.target.value), form.body || '');

  const handleInsertPromoLink = () => {
    const code = prompt('Code promo (ex: BIENVENUE20) :');
    const url  = prompt('URL de destination (https://) :');
    if (!code || !url) return;
    try {
      const parsed = new URL(url);
      if (!['https:', 'http:'].includes(parsed.protocol)) { addNotification('URL invalide', 'error'); return; }
      const safeCode = code.replace(/[<>"'&]/g, ESC);
      const html = `<a href="${parsed.href}" style="display:inline-block;background:#FFC400;color:#070A2A;padding:10px 24px;border-radius:8px;font-weight:700;text-decoration:none;margin:12px 0;">🎯 Utiliser le code ${safeCode}</a>`;
      insertAtCursor(bodyRef, '\n' + html + '\n', e => set('body', e.target.value), form.body || '');
      addNotification(`Lien promo "${safeCode}" inséré`);
    } catch { addNotification('URL invalide.', 'error'); }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { addNotification('Image trop lourde (max 500 Ko)', 'error'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const src = ev.target.result;
      const html = `\n<img src="${src}" alt="image" style="max-width:100%;border-radius:8px;margin:12px 0;" />\n`;
      insertAtCursor(bodyRef, html, ev2 => set('body', ev2.target.value), form.body || '');
      addNotification('Image insérée ✅');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ── Aperçu ────────────────────────────────────────────────────────────────────
  const companyName = (tenant?.name || 'Mon Entreprise').replace(/[<>"'&]/g, ESC);
  const previewBody = useMemo(() => sanitizeHtml(
    (form?.body || '')
      .replace(/{{prénom}}/g, 'Marie').replace(/{{nom}}/g, 'Dupont')
      .replace(/{{entreprise}}/g, companyName)
      .replace(/{{âge}}/g, '45').replace(/{{ville}}/g, 'Sion')
      .replace(/{{date}}/g, new Date().toLocaleDateString('fr-CH'))
      .replace(/{{mémoire}}/g, 'Client depuis 2020, amateur de café')
      .replace(/\n/g, '<br>')
  ), [form?.body, companyName]);

  // ── Stats ─────────────────────────────────────────────────────────────────────
  const inProdCount   = allItems.filter(t => isInProd(t)).length;
  const birthdayTpl   = getById(defaultActions.birthday);
  const assigningInfo = ACTIONS.find(a => a.id === assigningAction);
  const activeMoments = ACTIONS.filter(a => !!getById(defaultActions[a.id])).length;
  const birthdayAuto  = !!getById(defaultActions.birthday);
  const readyMoments  = ACTIONS.filter(a => a.id !== 'birthday' && !!getById(defaultActions[a.id])).length;

  // Ouvrir l'éditeur sur le message actuellement assigné à un moment
  const editAction = (actionId) => {
    const tpl = getById(defaultActions[actionId]);
    if (!tpl) return handleNew('email');
    handleEdit(tpl._baseId ? { ...tpl, _isBase: true } : { ...tpl, _isBase: false });
  };

  // Envoyer un moment (hors anniversaire) : ouvre le builder de campagne pré-rempli
  // avec le message, où l'utilisateur choisit l'audience, vérifie et ENVOIE (validation).
  const sendMoment = (action) => {
    const tpl = getById(defaultActions[action.id]);
    if (!tpl) return editAction(action.id);
    navigate('/campaigns/new', { state: { startStep: 2, preset: {
      name:      action.label,
      objective: action.id,
      subject:   tpl.subject || '',
      body:      tpl.body || '',
      sms:       tpl.channel === 'sms' ? (tpl.body || '') : '',
      tone:      tpl.tone || 'vouvoiement',
      channel:   tpl.channel || 'email',
      filters:   {},
    } } });
  };

  // Aperçu lisible (variables résolues) du message d'un moment
  const resolve = (s) => (s || '')
    .replace(/{{prénom}}/g, 'Marie').replace(/{{nom}}/g, 'Dupont')
    .replace(/{{entreprise}}/g, tenant?.name || 'Mon Entreprise');
  // Corps « propre » pour l'aperçu : on retire le HTML (images data:, liens…) et on aère
  const stripForPreview = (s) => resolve(s)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const closeEditor = () => { setForm(null); setSelected(null); setShowNew(false); };

  // Visibilité SMS
  const smsFmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' }); } catch { return null; } };
  const smsCredits = smsStatus?.credits ?? 0;
  const smsLow     = !!smsStatus?.configured && smsCredits <= 10;
  const smsEmpty   = !!smsStatus?.configured && smsCredits <= 0;

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="tpl-page">

      {/* ══ Header hero ══════════════════════════════════════════════════════════ */}
      <div className="tpl-page-hero">
        <div className="tpl-page-hero-nebula" />
        <div className="tpl-page-hero-top">
          <div className="tpl-page-hero-left">
            <div className="tpl-page-hero-icon-wrap">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.95)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div>
              <h1 className="tpl-page-hero-title">Messages automatiques</h1>
              <p className="tpl-page-hero-sub">Les mots qu'Appee envoie à ta place, au bon moment. Tu écris une fois — Appee s'occupe du reste.</p>
            </div>
          </div>
          <button className="tpl-page-hero-btn" onClick={() => handleNew()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Nouveau message
          </button>
        </div>
        <div className="tpl-page-hero-status">
          <span className="tpl-status-dot" />
          {(birthdayAuto || readyMoments > 0)
            ? <>
                {birthdayAuto && <>🔄 Anniversaire <strong>automatique</strong></>}
                {birthdayAuto && readyMoments > 0 && <> · </>}
                {readyMoments > 0 && <><strong>{readyMoments} message{readyMoments > 1 ? 's' : ''}</strong> prêt{readyMoments > 1 ? 's' : ''} à envoyer en 1 clic</>}
              </>
            : <>Configure tes messages ci-dessous</>}
        </div>
      </div>

      {/* ══ Visibilité SMS ═══════════════════════════════════════════════════════ */}
      {smsStatus && (
        <div className={`tpl-sms-status ${smsEmpty ? 'empty' : smsLow ? 'low' : ''} ${!smsStatus.configured ? 'off' : ''}`}>
          <div className="tpl-sms-status-main">
            <span className="tpl-sms-status-ic">📱</span>
            <div>
              <div className="tpl-sms-status-title">
                {smsStatus.configured
                  ? <>{smsEmpty ? <>Aucun SMS restant ce mois</> : <><strong>{smsCredits}</strong> SMS restants ce mois</>}
                      {smsStatus.limit ? <span className="tpl-sms-status-incl"> · {smsStatus.limit} inclus/mois</span> : null}</>
                  : <>SMS non configuré</>}
              </div>
              <div className="tpl-sms-status-sub">
                {smsStatus.configured
                  ? (smsStatus.birthday_enabled
                      ? <>✅ L'anniversaire part aussi en <strong>SMS</strong></>
                      : <>L'anniversaire ne part qu'en email — SMS anniversaire désactivé</>)
                  : <>Active le SMS dans Réglages pour envoyer aussi par SMS</>}
                {smsStatus.last_sent_at && smsFmtDate(smsStatus.last_sent_at) && (
                  <> · Dernier SMS envoyé le {smsFmtDate(smsStatus.last_sent_at)}</>
                )}
              </div>
            </div>
          </div>
          {(smsLow || smsEmpty) && (
            <div className="tpl-sms-status-alert">
              {smsEmpty ? '⚠️ Les anniversaires ne partiront plus en SMS' : `⚠️ Plus que ${smsCredits} SMS`}
            </div>
          )}
        </div>
      )}

      {/* ══ En continu — moments déclenchés par un client ════════════════════════ */}
      <div className="tpl-moments-section">
        <div className="tpl-section-label">💬 Tes messages <span className="tpl-section-sub">— l'anniversaire part tout seul le jour J ; les autres, tu les envoies en 1 clic quand tu veux (avec validation)</span></div>
        <div className="tpl-moments-grid">
          {ACTIONS.map(action => {
            const tpl = getById(defaultActions[action.id]);
            const active = !!tpl;
            return (
              <div
                key={action.id}
                className="tpl-moment"
                style={{ '--ac': action.color, '--ab': action.bg, '--abr': action.border }}
              >
                <div className="tpl-moment-top">
                  <span className="tpl-moment-ic">{action.icon}</span>
                  <div className="tpl-moment-head">
                    <div className="tpl-moment-name">{action.label}</div>
                    <div className="tpl-moment-when">{action.desc}</div>
                  </div>
                  {!active
                    ? <span className="tpl-moment-badge off">À configurer</span>
                    : action.id === 'birthday'
                      ? <span className="tpl-moment-badge on"><span className="tpl-moment-badge-dot" />🔄 Automatique</span>
                      : <span className="tpl-moment-badge ready">Prêt à envoyer</span>}
                </div>

                {active ? (
                  <div className="tpl-moment-prev">
                    {['email', 'mailjet'].includes(tpl.channel || 'email') && (
                      <div className="tpl-moment-prev-obj"><span className="tpl-moment-prev-lab">Objet</span>{resolve(tpl.subject)}</div>
                    )}
                    <div className="tpl-moment-prev-body">{stripForPreview(tpl.body)}</div>
                  </div>
                ) : (
                  <div className="tpl-moment-prev tpl-moment-prev-empty">Aucun message choisi pour ce moment.</div>
                )}

                <div className="tpl-moment-foot">
                  {active && <ChannelPastille channel={tpl.channel || 'email'} size="sm" />}
                  {active && action.id === 'birthday' && smsStatus?.birthday_enabled && (
                    <span className="tpl-moment-sms">📱 + SMS</span>
                  )}
                  {/* Action secondaire (texte) */}
                  {active && action.id === 'birthday' && (
                    <button className="tpl-moment-change" onClick={() => setAssigningAction(action.id)}>Changer</button>
                  )}
                  {active && action.id !== 'birthday' && (
                    <button className="tpl-moment-change" onClick={() => editAction(action.id)}>✏️ Modifier</button>
                  )}
                  {/* Action principale (bouton violet à droite) */}
                  {!active ? (
                    <button className="tpl-moment-edit" onClick={() => editAction(action.id)}>+ Choisir un message</button>
                  ) : action.id === 'birthday' ? (
                    <button className="tpl-moment-edit" onClick={() => editAction(action.id)}>✏️ Modifier</button>
                  ) : (
                    <button className="tpl-moment-edit" onClick={() => sendMoment(action)}>Envoyer →</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ══ Modal d'assignation ══════════════════════════════════════════════════ */}
      {assigningAction && (
        <div className="tpl-overlay" onClick={() => setAssigningAction(null)}>
          <div className="tpl-modal" onClick={e => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <div className="tpl-modal-title">
                {assigningInfo?.icon} Choisir le template pour <strong>{assigningInfo?.label}</strong>
              </div>
              <button className="tpl-modal-close" onClick={() => setAssigningAction(null)}>✕</button>
            </div>
            <div className="tpl-modal-list">
              {allItems
                .filter(t => ['email', 'mailjet'].includes(tplChannel(t)))
                .map(t => {
                  const id = t._baseId || String(t.id);
                  const current = String(defaultActions[assigningAction]) === String(id);
                  return (
                    <div
                      key={t._key}
                      className={`tpl-modal-item ${current ? 'active' : ''}`}
                      onClick={() => saveDefaultAction(assigningAction, id)}
                    >
                      <span className="tpl-modal-item-icon">{t.icon || '✉️'}</span>
                      <div className="tpl-modal-item-info">
                        <div className="tpl-modal-item-name">{t.name}</div>
                        <div className="tpl-modal-item-sub">{t.subject?.replace(/{{.*?}}/g, '…').slice(0, 52)}</div>
                        <ChannelPastille channel={tplChannel(t)} size="xs" />
                      </div>
                      {current && <span className="tpl-modal-check">✓</span>}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* ══ Bibliothèque (repliable) ═════════════════════════════════════════════ */}
      <div className="tpl-library">
        <button className="tpl-library-toggle" onClick={() => setLibOpen(o => !o)} aria-expanded={libOpen}>
          <span className="tpl-library-toggle-txt">
            📚 Bibliothèque
            <span className="tpl-library-toggle-sub">— tous tes messages ({allItems.length}) : retrouver, dupliquer ou en créer un</span>
          </span>
          <span className={`tpl-library-chev ${libOpen ? 'open' : ''}`}>▾</span>
        </button>

        {libOpen && (
        <div className="tpl-library-body">
          <div className="tpl-library-controls">
            <div className="tpl-channels">
              {CHANNELS.map(ch => (
                <button
                  key={ch.id}
                  className={`tpl-channel-btn ${channelFilter === ch.id ? 'active' : ''}`}
                  onClick={() => setChannelFilter(ch.id)}
                >
                  {ch.icon} {ch.label}
                </button>
              ))}
            </div>
            <input
              className="tpl-search"
              placeholder="🔍 Rechercher…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button className="tpl-library-new" onClick={() => handleNew()}>+ Nouveau</button>
          </div>

          {/* ─ Liste ─────────────────────────────────────────────────────────── */}
          <div className="tpl-list">

            {/* Templates de base */}
            {filteredItems.filter(t => t._isBase).length > 0 && (
              <>
                <div className="tpl-list-sec">✨ Inclus avec Appee</div>
                {filteredItems.filter(t => t._isBase).map(t => {
                  const prod   = isInProd(t);
                  const action = getActionFor(t);
                  return (
                    <div
                      key={t._key}
                      className={`tpl-item ${selected === t._baseId ? 'active' : ''}`}
                      style={{ '--tc': t.color, '--tb': t.bg, '--tbr': t.border }}
                      onClick={() => handleEdit(t)}
                    >
                      <span className="tpl-item-icon">{t.icon}</span>
                      <div className="tpl-item-info">
                        <div className="tpl-item-name">{t.name}</div>
                        <div className="tpl-item-sub">{t.subject?.replace(/{{.*?}}/g, '…')}</div>
                      </div>
                      <div className="tpl-item-right">
                        <ChannelPastille channel={t.channel || 'email'} />
                        {prod
                          ? <span className="tpl-badge-prod" title={action ? `Défaut : ${action.label}` : ''}>✓ Prod</span>
                          : <span className="tpl-badge-draft">Brouillon</span>
                        }
                        {action && <div className="tpl-item-action-hint">{action.icon} {action.label}</div>}
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Templates utilisateur */}
            {filteredItems.filter(t => !t._isBase).length > 0 && (
              <>
                <div className="tpl-list-sec" style={{ marginTop: 8 }}>Mes templates</div>
                {filteredItems.filter(t => !t._isBase).map(t => {
                  const prod   = isInProd(t);
                  const action = getActionFor(t);
                  const ch     = tplChannel(t);
                  return (
                    <div
                      key={t._key}
                      className={`tpl-item ${selected === t.id ? 'active' : ''}`}
                      onClick={() => handleEdit(t)}
                    >
                      <span className="tpl-item-icon">{t.icon || TYPE_LABELS[t.type]?.slice(0, 2) || '✉️'}</span>
                      <div className="tpl-item-info">
                        <div className="tpl-item-name">{t.name}</div>
                        <div className="tpl-item-sub">{t.subject?.replace(/{{.*?}}/g, '…')}</div>
                        <ChannelPastille channel={ch} />
                      </div>
                      <div className="tpl-item-right">
                        <ChannelPastille channel={tplChannel(t)} />
                        {prod
                          ? <span className="tpl-badge-prod" title={action ? `Défaut : ${action.label}` : ''}>✓ Prod</span>
                          : <span className="tpl-badge-draft">En cours</span>
                        }
                        {action && <div className="tpl-item-action-hint">{action.icon} {action.label}</div>}
                        <button
                          className="tpl-item-del"
                          onClick={e => { e.stopPropagation(); handleDelete(t.id); }}
                          disabled={deleting}
                          title="Supprimer"
                        >🗑</button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Empty state */}
            {filteredItems.length === 0 && (
              <div className="tpl-list-empty">
                <div style={{ fontSize: 32, marginBottom: 8 }}>
                  {channelFilter === 'sms' ? '📱' : channelFilter === 'landing' ? '🌐' : channelFilter === 'mailjet' ? '📧' : '🔍'}
                </div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {channelFilter === 'sms' ? 'Aucun template SMS' :
                   channelFilter === 'landing' ? 'Aucune landing page' :
                   channelFilter === 'mailjet' ? 'Aucun template Mailjet' : 'Aucun résultat'}
                </div>
                {['sms', 'landing', 'mailjet'].includes(channelFilter) && (
                  <button className="tpl-list-empty-btn" onClick={() => handleNew(channelFilter)}>
                    + Créer un template {channelFilter === 'sms' ? 'SMS' : channelFilter === 'landing' ? 'landing' : 'Mailjet'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* ══ Éditeur — modale plein écran ════════════════════════════════════════ */}
      {form && (
        <div className="tpl-edit-overlay" onClick={closeEditor}>
          <div className="tpl-edit-modal" onClick={e => e.stopPropagation()}>
            <button className="tpl-edit-modal-x" onClick={closeEditor} title="Fermer">✕</button>
              <Card className="tpl-editor">
                {/* Header */}
                <div className="tpl-editor-header">
                  <div className="tpl-editor-title">
                    {form._fromBase
                      ? `✏️ Personnaliser — ${form.name}`
                      : showNew ? '✨ Nouveau template' : `✏️ ${form.name || 'Modifier'}`}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {form.channel === 'sms' && (() => {
                      const len = (form.body || '').length;
                      const cls = len > 160 ? 'danger' : len > 140 ? 'warn' : '';
                      return (
                        <span className={`tpl-sms-counter ${cls}`}>
                          {len}/160 {len > 160 ? '⚠️ trop long' : len > 140 ? '⚡ limite' : '✓ 1 SMS'}
                        </span>
                      );
                    })()}
                    <ChannelPastille channel={form.channel || 'email'} size="sm" />
                  </div>
                </div>

                {/* Bannière contexte : template de base */}
                {form._fromBase && (
                  <div className="tpl-from-base-banner">
                    <span className="tpl-from-base-icon">✏️</span>
                    <div>
                      <strong>Vous personnalisez un template Appee.</strong>
                      {form._fromActionId && (
                        <> Une fois enregistré, il deviendra automatiquement votre template par défaut pour <strong>{ACTIONS.find(a => a.id === form._fromActionId)?.label}</strong>.</>
                      )}
                    </div>
                  </div>
                )}

                <div className="tpl-editor-form">
                  {/* Ligne 1 : nom + type + canal */}
                  <div className="tpl-row3">
                    <Input label="Nom du template" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Anniversaire tutoiement" />
                    <Select label="Type" value={form.type} onChange={e => set('type', e.target.value)}>
                      {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </Select>
                    <Select label="Canal" value={form.channel || 'email'} onChange={e => set('channel', e.target.value)}>
                      <option value="email">✉️ Email SMTP</option>
                      <option value="mailjet">📧 Email Mailjet</option>
                      <option value="sms">📱 SMS</option>
                      <option value="landing">🌐 Landing</option>
                    </Select>
                  </div>

                  {/* Objet email */}
                  {['email', 'mailjet'].includes(form.channel || 'email') && (
                    <Input label="Objet de l'email" value={form.subject || ''} onChange={e => set('subject', e.target.value)} placeholder="{{prénom}}, un message pour vous 🎉" />
                  )}

                  {/* ── Panneau Appee IA ────────────────────────────────────── */}
                  {form.channel !== 'landing' && (
                    <div className="tpl-ai-panel">
                      <div className="tpl-ai-header">
                        <div className="tpl-ai-brand">
                          <span className="tpl-ai-bee">🧞</span>
                          <div>
                            <div className="tpl-ai-title">Appee rédige pour vous</div>
                            <div className="tpl-ai-sub">
                              {form.channel === 'sms'
                                ? 'Générez un SMS personnalisé en un clic'
                                : "Choisissez un ton, l'IA génère l'objet et le corps du message"}
                            </div>
                          </div>
                        </div>
                        <Button
                          size="md"
                          loading={generating}
                          onClick={handleGenerate}
                          style={{ background: 'linear-gradient(135deg,#7C3AED,#5A35FF)', color: '#fff', border: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          ✨ Générer le message
                        </Button>
                      </div>
                      {/* Sélecteur de ton (email seulement) */}
                      {['email', 'mailjet'].includes(form.channel || 'email') && (
                        <div className="tpl-ai-tones">
                          <span className="tpl-ai-tone-label">Ton :</span>
                          {[
                            { v: 'vouvoiement', emoji: '🤝', label: 'Formel'    },
                            { v: 'tutoiement',  emoji: '😊', label: 'Amical'    },
                            { v: 'auto',        emoji: '🎯', label: 'Automatique' },
                          ].map(t => (
                            <button
                              key={t.v}
                              className={`tpl-ai-tone-btn ${(form.tone || 'vouvoiement') === t.v ? 'active' : ''}`}
                              onClick={() => set('tone', t.v)}
                            >
                              {t.emoji} {t.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Note SMS */}
                  {form.channel === 'sms' && (
                    <div className="tpl-channel-note tpl-channel-note-sms">
                      📱 <strong>Template SMS</strong> — texte uniquement, 160 caractères par message. Utilisez les variables pour personnaliser.
                    </div>
                  )}

                  {/* Note Landing */}
                  {form.channel === 'landing' && (
                    <div className="tpl-channel-note tpl-channel-note-landing">
                      🌐 <strong>Landing pages</strong> — fonctionnalité à venir. Notez l'URL de votre landing dans le corps du message.
                    </div>
                  )}

                  {/* Barre de variables */}
                  {form.channel !== 'landing' && (
                    <div className="tpl-toolbar">
                      <span className="tpl-toolbar-label">Insérer :</span>
                      {(form.channel === 'sms'
                        ? ['{{prénom}}', '{{nom}}', '{{entreprise}}', '{{ville}}']
                        : ['{{prénom}}', '{{nom}}', '{{âge}}', '{{entreprise}}', '{{ville}}', '{{date}}', '{{mémoire}}']
                      ).map(v => (
                        <code key={v} className="tpl-var" onClick={() => handleInsertVariable(v)}>{v}</code>
                      ))}
                      {['email', 'mailjet'].includes(form.channel || 'email') && (
                        <>
                          <button className="tpl-toolbar-btn tpl-btn-promo" onClick={handleInsertPromoLink}>🎯 Lien promo</button>
                          <button className="tpl-toolbar-btn tpl-btn-img" onClick={() => imgInputRef.current?.click()}>📷 Image</button>
                          <input ref={imgInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                        </>
                      )}
                    </div>
                  )}

                  {/* Tabs édition / aperçu */}
                  {form.channel !== 'landing' && (
                    <>
                      <div className="tpl-body-tabs">
                        <button className={`tpl-body-tab ${activeBodyTab === 'edit' ? 'active' : ''}`} onClick={() => setActiveBodyTab('edit')}>✏️ Éditeur</button>
                        <button className={`tpl-body-tab ${activeBodyTab === 'preview' ? 'active' : ''}`} onClick={() => setActiveBodyTab('preview')}>👁 Aperçu</button>
                      </div>

                      {activeBodyTab === 'edit' && (
                        <>
                          <Textarea
                            ref={bodyRef}
                            label=""
                            value={form.body || ''}
                            onChange={e => set('body', e.target.value)}
                            rows={form.channel === 'sms' ? 5 : 11}
                            placeholder={form.channel === 'sms' ? 'Bonjour {{prénom}}, nous vous souhaitons… (max 160 car.)' : 'Votre message… (HTML accepté pour images et liens)'}
                            style={{ fontFamily: 'monospace', fontSize: 13, borderColor: form.channel === 'sms' && (form.body||'').length > 160 ? '#EF4444' : undefined }}
                            maxLength={form.channel === 'sms' ? 160 : undefined}
                          />
                          {/* ── Aperçu SMS en direct ──────────────────────── */}
                          {form.channel === 'sms' && (
                            <div className="tpl-sms-mock-wrap">
                              <div className="tpl-sms-mock">
                                <div className="tpl-sms-mock-topbar">
                                  <div className="tpl-sms-mock-notch" />
                                  <span className="tpl-sms-mock-sender">
                                    {(tenant?.name || 'Mon Entreprise').slice(0, 14)}
                                  </span>
                                </div>
                                <div className="tpl-sms-mock-screen">
                                  <div className="tpl-sms-mock-bubble">
                                    {(form.body || '')
                                      .replace(/{{prénom}}/g, 'Marie')
                                      .replace(/{{nom}}/g, 'Dupont')
                                      .replace(/{{entreprise}}/g, tenant?.name || 'Mon Entreprise')
                                      .replace(/{{ville}}/g, 'Sion')
                                      || <span style={{ opacity: 0.4, fontStyle: 'italic' }}>Aperçu du SMS…</span>
                                    }
                                  </div>
                                </div>
                                <div
                                  className="tpl-sms-mock-count"
                                  style={{ color: (form.body||'').length > 160 ? '#EF4444' : (form.body||'').length > 140 ? '#F59E0B' : '#10B981' }}
                                >
                                  {(form.body||'').length}/160
                                  {(form.body||'').length > 160 ? ' — trop long ⚠️' : ''}
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {activeBodyTab === 'preview' && (
                        <div className="tpl-preview-box">
                          {['email', 'mailjet'].includes(form.channel || 'email') && (
                            <div className="tpl-preview-subject">
                              Objet : <strong>
                                {(form.subject || '(vide)')
                                  .replace(/{{prénom}}/g, 'Marie')
                                  .replace(/{{entreprise}}/g, tenant?.name || 'Mon Entreprise')}
                              </strong>
                            </div>
                          )}
                          <div
                            className="tpl-preview-body"
                            dangerouslySetInnerHTML={{
                              __html: form.channel === 'sms'
                                ? (form.body || '').replace(/{{prénom}}/g, 'Marie').replace(/{{nom}}/g, 'Dupont').replace(/{{entreprise}}/g, tenant?.name || 'Mon Entreprise').replace(/\n/g, '<br>')
                                : previewBody
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}

                  <div className="tpl-editor-btns">
                    <Button onClick={handleSave} loading={saving}>
                      {form._fromBase ? '💾 Enregistrer mon template' : showNew ? 'Créer le template' : 'Sauvegarder'}
                    </Button>
                    <Button variant="ghost" onClick={() => { setForm(null); setSelected(null); setShowNew(false); }}>Annuler</Button>
                  </div>
                </div>
              </Card>
          </div>
        </div>
      )}

      {/* ══ Dates clés — messages ponctuels ══════════════════════════════════════ */}
      <div className="tpl-inspirations">
        <div className="tpl-section-label">📅 Dates clés <span className="tpl-section-sub">— messages ponctuels prêts à envoyer le moment venu</span></div>
        <div className="tpl-inspi-grid">
          {SEASONAL_TEMPLATES.map((t, i) => (
            <div
              key={i}
              className="tpl-inspi-card"
              style={{ '--ib': t.bg, '--ibr': t.border, '--ic': t.color }}
              onClick={() => {
                setForm({ name: t.name, type: t.type, tone: t.tone, subject: t.subject, body: t.body, channel: 'email' });
                setSelected(null); setShowNew(true); setActiveBodyTab('edit');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <div className="tpl-inspi-icon">{t.icon}</div>
              <div className="tpl-inspi-season">{t.season}</div>
              <div className="tpl-inspi-name">{t.name}</div>
              <div className="tpl-inspi-cta">Préparer →</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
